import { useCallback, useState } from 'react';

import { AgreementImportWizardLayout } from './agreement-import-wizard-layout';
import {
  AgreementImportWizardState,
  INITIAL_AGREEMENT_IMPORT_WIZARD_STATE,
  STEP_APPROVE,
  STEP_CONTRACTS,
  STEP_DONE,
  STEP_REVIEW,
  STEP_SESSION,
  STEP_UPLOAD,
} from './agreement-import-wizard.types';
import { StepSession } from './steps/step-session';
import { StepUpload } from './steps/step-upload';
import { StepContracts } from './steps/step-contracts';
import { StepReview } from './steps/step-review';
import { StepApprove } from './steps/step-approve';
import { StepDone } from './steps/step-done';

/**
 * Bulk lease-import wizard (Phase 7). Mirrors the six backend stages: start session
 * (Phase 0/1 gates), upload source rows (Phase 2/2b), contracts overview, per-contract
 * review & correction (Phase 4), approve & transfer (Phase 5, Draft-only), and a
 * session summary.
 */
export function AgreementImportWizard() {
  const [state, setState] = useState<AgreementImportWizardState>(INITIAL_AGREEMENT_IMPORT_WIZARD_STATE);

  const handleSessionCreated = useCallback((sessionId: string) => {
    setState((prev) => ({ ...prev, sessionId, currentStep: STEP_UPLOAD }));
  }, []);

  const handleRowsImported = useCallback(() => {
    setState((prev) => ({ ...prev, currentStep: STEP_CONTRACTS }));
  }, []);

  const handleBackFromUpload = useCallback(() => {
    setState((prev) => ({ ...prev, currentStep: STEP_SESSION }));
  }, []);

  const handleReviewContract = useCallback((contractId: string) => {
    setState((prev) => ({
      ...prev,
      activeContractId: contractId,
      reviewReturnStep: prev.currentStep,
      currentStep: STEP_REVIEW,
    }));
  }, []);

  const handleBackFromContracts = useCallback(() => {
    setState((prev) => ({ ...prev, currentStep: STEP_UPLOAD }));
  }, []);

  const handleBackFromReview = useCallback(() => {
    setState((prev) => ({ ...prev, activeContractId: null, currentStep: prev.reviewReturnStep }));
  }, []);

  const handleContinueToApprove = useCallback(() => {
    setState((prev) => ({ ...prev, currentStep: STEP_APPROVE }));
  }, []);

  const handleBackFromApprove = useCallback(() => {
    setState((prev) => ({ ...prev, currentStep: STEP_CONTRACTS }));
  }, []);

  const handleFinish = useCallback(() => {
    setState((prev) => ({ ...prev, currentStep: STEP_DONE }));
  }, []);

  const handleBackFromDone = useCallback(() => {
    setState((prev) => ({ ...prev, currentStep: STEP_APPROVE }));
  }, []);

  const handleImportAnother = useCallback(() => {
    setState(INITIAL_AGREEMENT_IMPORT_WIZARD_STATE);
  }, []);

  const renderStep = () => {
    switch (state.currentStep) {
      case STEP_SESSION:
        return <StepSession onSessionCreated={handleSessionCreated} />;
      case STEP_UPLOAD:
        return state.sessionId ? (
          <StepUpload sessionId={state.sessionId} onRowsImported={handleRowsImported} onBack={handleBackFromUpload} />
        ) : null;
      case STEP_CONTRACTS:
        return state.sessionId ? (
          <StepContracts
            sessionId={state.sessionId}
            onReviewContract={handleReviewContract}
            onContinueToApprove={handleContinueToApprove}
            onBack={handleBackFromContracts}
          />
        ) : null;
      case STEP_REVIEW:
        return state.sessionId && state.activeContractId ? (
          <StepReview sessionId={state.sessionId} contractId={state.activeContractId} onBack={handleBackFromReview} />
        ) : null;
      case STEP_APPROVE:
        return state.sessionId ? (
          <StepApprove
            sessionId={state.sessionId}
            onReviewContract={handleReviewContract}
            onFinish={handleFinish}
            onBack={handleBackFromApprove}
          />
        ) : null;
      case STEP_DONE:
        return state.sessionId ? (
          <StepDone sessionId={state.sessionId} onImportAnother={handleImportAnother} onBack={handleBackFromDone} />
        ) : null;
      default:
        return null;
    }
  };

  return <AgreementImportWizardLayout currentStep={state.currentStep}>{renderStep()}</AgreementImportWizardLayout>;
}
